/**
 * Accounts and subscriptions for Ceiling Reading: the session (token + refresh
 * token), the server-side entitlement (which voices this account may use) and
 * the bridge to the native app's in-app purchases.
 */
import { isWallOsAppUserAgent } from '../../waitlist/gate';

export type PaidPlan = 'FREE' | 'PRO' | 'ELITE';

export interface Entitlement {
  appUserId: string;
  plan: PaidPlan;
  paidVoices: boolean;
  billingSource: 'stripe' | 'apple' | 'google' | 'comp' | null;
  renewsOrEnds: string | null;
}

const AUTH_KEYS = [
  'wallos_auth_token', 'wallos_user_id', 'wallos_user_email',
  'wallos_subscription_status', 'wallos_user_plan', 'wallos_refresh_token',
];

export function isNativeApp(): boolean {
  return typeof navigator !== 'undefined' && isWallOsAppUserAgent(navigator.userAgent || '');
}

/** The app on a TV or projector (App.tsx adds "WallOSTV/1" to the user agent). */
export function isTvApp(): boolean {
  return typeof navigator !== 'undefined' && /WallOSTV\//.test(navigator.userAgent || '');
}

export function nativePlatform(): 'ios' | 'android' | null {
  if (typeof navigator === 'undefined') return null;
  const m = /WallOSApp\/(ios|android)/.exec(navigator.userAgent || '');
  return (m?.[1] as 'ios' | 'android') ?? null;
}

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};

export function sessionToken(): string | null {
  return read('wallos_auth_token');
}

export function saveSession(data: { token: string; refreshToken?: string; user?: { id?: string; email?: string; plan?: string; subscriptionStatus?: string } }, email?: string) {
  localStorage.setItem('wallos_auth_token', data.token);
  if (data.refreshToken) localStorage.setItem('wallos_refresh_token', data.refreshToken);
  if (data.user?.id) localStorage.setItem('wallos_user_id', data.user.id);
  const mail = data.user?.email || email;
  if (mail) localStorage.setItem('wallos_user_email', mail);
  localStorage.setItem('wallos_subscription_status', data.user?.subscriptionStatus || 'inactive');
  localStorage.setItem('wallos_user_plan', data.user?.plan || 'FREE');
}

export function signOut() {
  for (const k of AUTH_KEYS) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

async function refreshSession(): Promise<boolean> {
  const rt = read('wallos_refresh_token');
  if (!rt) return false;
  try {
    const r = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!r.ok) return false;
    saveSession(await r.json());
    return true;
  } catch {
    return false;
  }
}

/** fetch with the session token, refreshing it once on a 401. */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const go = () => {
    const t = sessionToken();
    return fetch(url, { ...init, headers: { ...(init.headers as Record<string, string> | undefined), ...(t ? { Authorization: `Bearer ${t}` } : {}) } });
  };
  let r = await go();
  if (r.status === 401 && (await refreshSession())) r = await go();
  return r;
}

export type SessionState = 'signed-in' | 'signed-out' | 'offline';

/** Is there a usable session? Expired sessions are refreshed, dead ones cleared. */
export async function checkSession(): Promise<SessionState> {
  if (!sessionToken() && !read('wallos_refresh_token')) return 'signed-out';
  try {
    const r = await authedFetch('/api/iap/status');
    if (r.ok) return 'signed-in';
    if (r.status === 401 || r.status === 403 || r.status === 404) {
      signOut();
      return 'signed-out';
    }
    return 'offline';
  } catch {
    return 'offline';
  }
}

export async function loadEntitlement(): Promise<Entitlement | null> {
  if (!sessionToken()) return null;
  try {
    const r = await authedFetch('/api/iap/status');
    if (!r.ok) return null;
    return (await r.json()) as Entitlement;
  } catch {
    return null;
  }
}

// ── Native app bridge (App.tsx) ─────────────────────────────────────────────
// The apps sell nothing through the App Store or Google Play: plans and voice
// packs are Stripe only, and a plan on the account works on every platform.
// Web → app:  {t:'billing:caps'} {t:'checkout:open', url} {t:'reader:open'}
// App → web:  window event 'wallos-native' with detail
//             {t:'billing:caps', platform, storefront, readerLink, androidLinks, tv}
//             {t:'reader:result', ok} {t:'app:active'}
// App builds before 1.3.0 never answer billing:caps, so they sell nothing.

export type PackageId = 'pro_monthly' | 'pro_yearly' | 'elite_monthly' | 'elite_yearly' | 'voice_pack';

export interface BillingCaps {
  platform: 'ios' | 'android';
  /** App Store storefront (StoreKit, ISO alpha-3 such as "USA"); null on Android. */
  storefront: string | null;
  /** Apple granted the reader-app External Link Account entitlement (build flag). */
  readerLink: boolean;
  /** Enrolled in Google Play's external content links program (build flag). */
  androidLinks: boolean;
  /** A TV or projector: Play and Amazon TV review allow no purchase UI at all. */
  tv?: boolean;
}

export type NativeMessage =
  | ({ t: 'billing:caps' } & BillingCaps)
  | { t: 'reader:result'; ok: boolean }
  | { t: 'app:active' };

/**
 * What the plans page may offer inside the app:
 *  - 'checkout': prices and Stripe Checkout in the browser, tied to the account
 *    (iOS on the US storefront, where Apple allows purchase links; Android only
 *    once enrolled in Google's external content links program).
 *  - 'reader': no prices; one Apple-sheet link to the wallos.io account page
 *    (iOS elsewhere, once Apple grants the reader-app entitlement).
 *  - 'none': no prices, buttons or links; sign in with a WallOS account.
 *    Always on TVs and projectors.
 */
export type AppBillingMode = 'checkout' | 'reader' | 'none';

export function appBillingMode(caps: BillingCaps | null | undefined): AppBillingMode {
  if (!caps || caps.tv) return 'none';
  if (caps.platform === 'ios') {
    const c = (caps.storefront || '').toUpperCase();
    if (c === 'USA' || c === 'US') return 'checkout';
    return caps.readerLink ? 'reader' : 'none';
  }
  return caps.androidLinks ? 'checkout' : 'none';
}

export function postNative(msg: Record<string, unknown>): boolean {
  const rn = (window as Window & { ReactNativeWebView?: { postMessage: (m: string) => void } }).ReactNativeWebView;
  if (!rn) return false;
  rn.postMessage(JSON.stringify(msg));
  return true;
}

export function onNative(handler: (msg: NativeMessage) => void): () => void {
  const fn = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (detail && typeof detail.t === 'string') handler(detail as NativeMessage);
  };
  window.addEventListener('wallos-native', fn);
  return () => window.removeEventListener('wallos-native', fn);
}
